import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { SearchQuery } from '../models/search-query.model';
import {
  ServiceRepairFinderFilter,
  ServiceRepairFacetOption,
  SERVICE_REPAIR_AIRFRAME_OPTIONS,
  SERVICE_REPAIR_AIRFRAME_MANUFACTURER_OPTIONS,
  SERVICE_REPAIR_AVIONICS_OPTIONS,
  SERVICE_REPAIR_AVIONICS_MANUFACTURER_OPTIONS,
  SERVICE_REPAIR_POWERPLANT_OPTIONS,
  SERVICE_REPAIR_POWERPLANT_MANUFACTURER_OPTIONS,
  SERVICE_REPAIR_PROPELLER_OPTIONS,
  SERVICE_REPAIR_PROPELLER_MANUFACTURER_OPTIONS,
  SERVICE_REPAIR_INTERIOR_OPTIONS,
  SERVICE_REPAIR_EXTERIOR_OPTIONS,
  SERVICE_REPAIR_ROTOR_OPTIONS
} from '../../shared/models/service-repair-finder-filter.model';
import { ServiceRepairFinderFiltersService } from '../../shared/services/service-repair-finder-filters.service';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';

/**
 * Group keys on ServiceRepairFinderFilter that hold an array of selected tag names.
 */
type FacetGroup = 'airframe' | 'airframeManufacturers' | 'avionics' | 'avionicsManufacturers'
  | 'powerplant' | 'powerplantManufacturers' | 'propeller' | 'propellerManufacturers'
  | 'interior' | 'exterior' | 'rotor';

/**
 * Filter UI for the Service/Repair Finder, mirroring {@link FboFinderFilterListComponent}. Service/repair
 * has many facets (the Maintenance categories, each with a Manufacturers subsection), so its groups are
 * collapsible accordion sections with a selected-count badge, and a selected-filter chip row. Reads and
 * writes the shared {@link ServiceRepairFinderFiltersService} state and emits `filtersChange`.
 */
@Component({
  selector: 'app-service-repair-finder-filter-list',
  templateUrl: './service-repair-finder-filter-list.component.html',
  styleUrls: ['./service-repair-finder-filter-list.component.scss']
})
export class ServiceRepairFinderFilterListComponent implements OnInit, OnDestroy {
  @Input() compact: boolean;
  @Input() query: SearchQuery;
  @Output() filtersChange = new EventEmitter<void>();

  /**
   * The collapsible facet sections, in display order: the group key on ServiceRepairFinderFilter, the header
   * label, and the checkbox options. Service groups use AND logic and Manufacturer subsections use OR logic
   * (see filterServiceRepairs).
   */
  readonly sections: {
    key: FacetGroup;
    title: string;
    options: ServiceRepairFacetOption[];
    manufacturers?: { key: FacetGroup; title: string; options: ServiceRepairFacetOption[] };
  }[] = [
    { key: 'airframe', title: 'Airframe', options: SERVICE_REPAIR_AIRFRAME_OPTIONS,
      manufacturers: { key: 'airframeManufacturers', title: 'Airframe Manufacturers', options: SERVICE_REPAIR_AIRFRAME_MANUFACTURER_OPTIONS } },
    { key: 'avionics', title: 'Avionics', options: SERVICE_REPAIR_AVIONICS_OPTIONS,
      manufacturers: { key: 'avionicsManufacturers', title: 'Avionics Manufacturers', options: SERVICE_REPAIR_AVIONICS_MANUFACTURER_OPTIONS } },
    { key: 'powerplant', title: 'Powerplant', options: SERVICE_REPAIR_POWERPLANT_OPTIONS,
      manufacturers: { key: 'powerplantManufacturers', title: 'Powerplant Manufacturers', options: SERVICE_REPAIR_POWERPLANT_MANUFACTURER_OPTIONS } },
    { key: 'propeller', title: 'Propeller', options: SERVICE_REPAIR_PROPELLER_OPTIONS,
      manufacturers: { key: 'propellerManufacturers', title: 'Propeller Manufacturers', options: SERVICE_REPAIR_PROPELLER_MANUFACTURER_OPTIONS } },
    { key: 'interior', title: 'Interior', options: SERVICE_REPAIR_INTERIOR_OPTIONS },
    { key: 'exterior', title: 'Exterior', options: SERVICE_REPAIR_EXTERIOR_OPTIONS },
    { key: 'rotor', title: 'Rotor', options: SERVICE_REPAIR_ROTOR_OPTIONS }
  ];

  /**
   * Per-section expand/collapse state. There are far more service/repair facets than fit on one screen, so
   * sections start collapsed; any section that already has selected filters is auto-expanded on first load so
   * restored/active filters are visible. Each header shows a count badge.
   */
  expanded: { [group: string]: boolean } = {
    airframe: false,
    airframeManufacturers: false,
    avionics: false,
    avionicsManufacturers: false,
    powerplant: false,
    powerplantManufacturers: false,
    propeller: false,
    propellerManufacturers: false,
    interior: false,
    exterior: false,
    rotor: false
  };
  private _expansionInitialized = false;

  /** Max number of selected-filter chips to render before collapsing the rest into a "+N more" marker. */
  readonly maxChips = 5;

  /** The chips actually shown (first {@link maxChips} selected filters, in section + option order). */
  visibleChips: { group: FacetGroup; value: string; label: string }[] = [];

  /** How many selected filters are hidden beyond {@link maxChips} (0 = none hidden). */
  overflowCount = 0;

  serviceRepairFinderFilter = new ServiceRepairFinderFilter();
  private _subscription: Subscription;

  /**
   * True when a business name has been entered but is too short to count toward a search. Mirrors the
   * `serviceRepairKeyword.length >= 3` gate in {@link SearchQuery.validate}, so the hint shows exactly while
   * the entered name would be ignored.
   */
  get nameTooShort(): boolean {
    const trimmed = this.serviceRepairFinderFilter.name ? this.serviceRepairFinderFilter.name.trim() : '';
    return trimmed.length > 0 && trimmed.length < 3;
  }

  constructor(private _serviceRepairFinderFiltersService: ServiceRepairFinderFiltersService,
              private _analyticsService: AnalyticsService) { }

  ngOnInit() {
    this._subscription = this._serviceRepairFinderFiltersService.serviceRepairFinderFilter.subscribe(f => {
      this.serviceRepairFinderFilter = f;
      this._recomputeChips();
      // Auto-expand sections with active filters once (on first load); don't fight the user's manual toggles
      // on subsequent emissions (each checkbox edit re-emits the same filter).
      if (!this._expansionInitialized) {
        this._expansionInitialized = true;
        this.sections.forEach(s => {
          const mfr = s.manufacturers;
          const mfrHas = mfr ? !!(f && f[mfr.key] && f[mfr.key].length) : false;
          const svcHas = !!(f && f[s.key] && f[s.key].length);
          this.expanded[s.key] = svcHas || mfrHas;   // parent opens if anything inside (service or mfr) is selected
          if (mfr) {
            this.expanded[mfr.key] = mfrHas;          // manufacturer subsection opens only if it has selections
          }
        });
      }
    });
    this._analyticsService.pageView('airport_landingpage_servicerepair', 'Service/Repair Finder Landing Page filters');
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  isFacetSelected(group: FacetGroup, value: string): boolean {
    return this.serviceRepairFinderFilter[group].indexOf(value) !== -1;
  }

  onToggleFacet(group: FacetGroup, value: string, checked: boolean) {
    const selected = this.serviceRepairFinderFilter[group];
    const i = selected.indexOf(value);
    if (checked && i === -1) {
      selected.push(value);
    } else if (!checked && i !== -1) {
      selected.splice(i, 1);
    }
    this._emit();
  }

  onSelectAll(group: FacetGroup, options: ServiceRepairFacetOption[]) {
    this.serviceRepairFinderFilter[group] = options.map(o => o.value);
    this._emit();
  }

  onClearAll(group: FacetGroup) {
    this.serviceRepairFinderFilter[group] = [];
    this._emit();
  }

  onNameChange() {
    this._emit();
  }

  toggleSection(group: FacetGroup) {
    this.expanded[group] = !this.expanded[group];
  }

  /** Number of selected filters in a single group (shown as the count badge on that group's header). */
  selectedCount(group: FacetGroup): number {
    const selected = this.serviceRepairFinderFilter[group];
    return selected ? selected.length : 0;
  }

  /**
   * Total selected filters for a parent type section — its own services plus its nested Manufacturers
   * subsection — so the parent badge still reflects manufacturer selections while the subsection is collapsed.
   */
  sectionBadgeCount(section: { key: FacetGroup; manufacturers?: { key: FacetGroup } }): number {
    let count = this.selectedCount(section.key);
    if (section.manufacturers) {
      count += this.selectedCount(section.manufacturers.key);
    }
    return count;
  }

  trackByChip(index: number, chip: { value: string }): string {
    return chip.value;
  }

  /**
   * Rebuilds the selected-filter chip list from the current filter state (called on every filter change).
   * Chips are collected in section + option order; only the first {@link maxChips} are shown, the rest are
   * summarized by {@link overflowCount}.
   */
  private _recomputeChips() {
    const chips: { group: FacetGroup; value: string; label: string }[] = [];
    const collect = (key: FacetGroup, options: ServiceRepairFacetOption[]) => {
      const selected = this.serviceRepairFinderFilter[key] || [];
      options.forEach(option => {
        if (selected.indexOf(option.value) !== -1) {
          chips.push({ group: key, value: option.value, label: option.label });
        }
      });
    };
    this.sections.forEach(section => {
      collect(section.key, section.options);
      if (section.manufacturers) {
        collect(section.manufacturers.key, section.manufacturers.options);
      }
    });
    this.visibleChips = chips.slice(0, this.maxChips);
    this.overflowCount = Math.max(0, chips.length - this.maxChips);
  }

  private _emit() {
    // Keep the query's filter in sync as the user edits, so a business name entered here counts toward search
    // validation (see SearchQuery.validate) even before the query is serialized/submitted.
    if (this.query) {
      this.query.serviceRepairFinderFilter = this.serviceRepairFinderFilter;
    }
    this._serviceRepairFinderFiltersService.updateServiceRepairFinderFilters(this.serviceRepairFinderFilter);
    this.filtersChange.emit();
  }
}
